/**
 * HITL service layer.
 *
 * Why a dedicated file?
 * ---------------------
 * The generic `api.ts` client carries the raw contract surface for every
 * endpoint. This file wraps the HITL-specific ones in higher-level helpers
 * that the dashboard + modal components actually want: filter-in-one-call
 * queries, an "act on a hit then refresh its audit trail" convenience, and
 * a typed `ActionResult` shape with both the server response and the audit
 * entry the mock synthesised so callers don't have to re-query.
 *
 * Keep this thin — any real business logic belongs in the components so
 * tests can assert on the behaviour directly, not on a service layer
 * intermediary.
 */

import { api } from './api';
import type {
  AuditEntry,
  HitlActionRequest,
  HitlActionResponse,
  HitlItem,
  HitlListQuery,
} from './types';

export interface ActionResult {
  response: HitlActionResponse;
  /**
   * The optimistic audit entry the UI should slot in immediately. Mirrors
   * what the server persisted so the "apply then re-fetch" round-trip is
   * optional — the dashboard uses it to update the timeline without a
   * second request.
   */
  audit: AuditEntry;
}

export const hitlService = {
  list(filters: HitlListQuery = {}): Promise<HitlItem[]> {
    return api.queryHits(filters);
  },

  getAudit(hitId: string): Promise<AuditEntry[]> {
    return api.getAuditTrail(hitId);
  },

  /**
   * Send a reviewer action and synthesise the matching audit entry so the
   * caller can splice it into the visible timeline without a second round
   * trip. The server is still the source of truth — call `getAudit(hitId)`
   * afterwards if you need confirmation.
   */
  async act(hitId: string, action: HitlActionRequest): Promise<ActionResult> {
    const response = await api.postHitAction(hitId, action);
    const audit: AuditEntry = {
      audit_id: response.audit_id,
      hit_id: hitId,
      reviewer_id: action.reviewer_id,
      action_type: action.type,
      payload: action.payload,
      comment: action.comment,
      timestamp: new Date().toISOString(),
    };
    return { response, audit };
  },

  /** Derived status for the hit row after an action settles successfully. */
  resolvedStatus(action: HitlActionRequest['type']): HitlItem['status'] {
    return action === 'escalate' ? 'escalated' : 'resolved';
  },
};
